import * as React from 'react';
import { CheckboxInline, Options } from '@common/components';
import { Control, Errors, Form, actions } from 'react-redux-form';

import { OptionsViewModel } from '@models/common.models';

import { getDefaultErrorMessages, isSellersCoverageRequiredState } from '@common/validators';

import { ProductGroupViewModel, PriceAdjustmentViewModel, EmployeeDiscountViewModel } from '@models/Product';
import { Dispatch } from 'react-redux';
import { isPropertyTypeElligibleForSellersCoverage } from '@common/validators';
import { ContractViewModel, ContractOrderType } from '@models/Contract';
import { compareOrder } from '@common/comparators';
import FormNames from '@constants/FormNames.enum';
import { EditPermissions, EditableFields } from '@models/EditPermissions';

export interface ProductNCoveragesComponentPropertiesAttributes {
  readonly productGroups: Array<ProductGroupViewModel>;
  readonly products: Array<OptionsViewModel>;
  readonly priceAdjustments: Array<PriceAdjustmentViewModel>;
  readonly hasSellersCoverage: boolean;
  readonly isQuote: boolean;

  readonly propertyTypeId: number; // from ownProps
  readonly stateAbbreviation: string; // from ownProps
  readonly zipCode: string; // from ownProps
  readonly employeeDiscounts: Array<EmployeeDiscountViewModel>; // employee discounts from ownProps
  readonly isOrderEdit?: boolean;
  readonly selectedProductId?: number;
  readonly selectedGroupId?: number;
  readonly selectedPriceAdjustment?: PriceAdjustmentViewModel;
  readonly isEmployeeDiscountEnabled: boolean;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly selectedEmployeeDiscount?: EmployeeDiscountViewModel;
  readonly overridePriceAdjustmentAmount?: number;
  readonly orderType?: ContractOrderType;
  readonly priceAdjustmentNotes?: string;
  readonly contractVM: ContractViewModel;
  readonly isOpenContract?: boolean;
}

export interface ProductNCoveragesComponentProperties extends ProductNCoveragesComponentPropertiesAttributes {
  readonly selectProductGroup: (productGroup: ProductGroupViewModel) => void;
  readonly selectProduct: (product: OptionsViewModel, group: number) => void;
  readonly toggleSellersCoverage: () => void;
  readonly onSaveClick: (model: CoverageForm) => void;
  readonly selectEmployeeDiscount: (employeeDiscount: EmployeeDiscountViewModel) => void;
}

export interface CoverageForm {
  priceAdjustmentAmount: number;
  priceAdjustmentNotes: string;
}

export class ProductNCoveragesComponent extends React.Component<ProductNCoveragesComponentProperties> {
  formDispatch: Dispatch<CoverageForm>;

  constructor(props: ProductNCoveragesComponentProperties) {
    super(props);
  }

  componentDidMount() {
    if (this.props.selectedGroupId) {
      this.formDispatch(actions.change(FormNames.CoverageForm + '.product.productGroupId', this.props.selectedGroupId));
    }
    if (this.props.selectedProductId) {
      this.formDispatch(actions.change(FormNames.CoverageForm + '.product.id', this.props.selectedProductId));
    }
  }

  canToggleSellersCoverage = (): boolean => {
    return !isSellersCoverageRequiredState(this.props.stateAbbreviation) && this.isSellersCoverageAvailable();
  };

  isSellersCoverageAvailable() {
    if (this.props.orderType === ContractOrderType.DTC) {
      return false;
    }
    return isPropertyTypeElligibleForSellersCoverage(this.props.propertyTypeId);
  }

  renderOptions(options: Array<OptionsViewModel>, sortByOrder?: boolean) {
    const sorted = sortByOrder ? options.sort(compareOrder) : options;
    return sorted.map((option: OptionsViewModel, index: number) => {
      return (
        <option key={option.id + option.value} value={option.id}>
          {option.value}
        </option>
      );
    });
  }

  renderProductOptions() {
    if (!this.props.selectedGroupId) {
      return <option value="-1">Select a Group</option>;
    } else if (this.props.products.length === 0) {
      return <option value="-1">No Products available</option>;
    }

    return <option value="-1">Select one ...</option>;
  }

  handleSelectProductGroup = (event: React.SyntheticEvent<HTMLSelectElement>): void => {
    if (!(event.currentTarget || {}).value) {
      return;
    }
    const productGroupId = +event.currentTarget.value;

    this.setProductGroup(productGroupId);
  };

  setProductGroup = (productGroupId: number) => {
    const productGroup = this.props.productGroups.find(x => x.id === productGroupId);
    if ((!productGroup && productGroupId >= 0) || productGroupId === this.props.selectedGroupId) {
      return;
    }

    this.props.selectProductGroup(productGroup || ({ id: productGroupId } as ProductGroupViewModel));
    this.formDispatch(actions.change(FormNames.CoverageForm + '.product.productGroupId', productGroupId));
  };

  componentDidUpdate(prevProps: ProductNCoveragesComponentProperties) {
    if (prevProps.productGroups.length === 0 && this.props.productGroups.length === 1) {
      this.setProductGroup(this.props.productGroups[0].id);
    }
  }

  // handleSelectProductChannel = (event: React.SyntheticEvent<HTMLSelectElement>): void => {
  //   if (!(event.currentTarget || {}).value) {
  //     return;
  //   }
  //   const productChannelId = +event.currentTarget.value;
  //   const channel = this.props.productChannels.find(x => x.id === productChannelId);

  //   if (!channel && productChannelId >= 0) {
  //     return;
  //   }
  //   this.props.selectProductChannel(channel || ({ id: productChannelId } as ProductChannelViewModel));
  // };

  handleSelectProduct = (event: React.SyntheticEvent<HTMLSelectElement>): void => {
    if (this.props.selectedGroupId === undefined) {
      return;
    }

    if (!(event.currentTarget || {}).value) {
      return;
    }
    const productId = +event.currentTarget.value;
    const product = this.props.products.find(x => x.id === productId);
    if (!product && productId >= 0) {
      return;
    }

    this.props.selectProduct(product || ({ id: productId } as OptionsViewModel), this.props.selectedGroupId);
  };

  attachDispatch(dispatch: Dispatch<CoverageForm>) {
    this.formDispatch = dispatch;
  }

  isSelected = (key: number) => {
    return key !== undefined && +key !== -1;
  };

  render() {
    return (
      <Form
        model="coverageForm"
        getDispatch={(dispatch: Dispatch<CoverageForm>) => this.attachDispatch(dispatch)}
        validateOn="submit"
        onSubmit={this.props.onSaveClick}
        hideNativeErrors={true} /*onSubmit={this.props.handleSave}*/
      >
        <div className="row">
          <div className="col-md-12">
            <h4>Product & Coverages</h4>
          </div>
        </div>
        <div className="row">
          <div className="form-control col-md-12">
            <label htmlFor="coverageForm.product.productGroupId">Group</label>
            <Control.select
              value={this.props.selectedGroupId}
              onChange={this.handleSelectProductGroup}
              model=".product.productGroupId"
              validators={{
                selection: this.isSelected
              }}
              disabled={
                this.props.isOrderEdit &&
                !EditPermissions.CanEditField(EditableFields.CoverageOptions, this.props.isOpenContract)
              }
            >
              <Options options={this.props.productGroups} />
            </Control.select>
            <Errors
              className="error error-pad"
              model=".product.productGroupId"
              show={{ touched: true, focus: false }}
              messages={getDefaultErrorMessages()}
            />
          </div>
          <div className="form-control col-md-12">
            <label htmlFor="coverageForm.product.id">Product</label>
            <Control.select
              model=".product.id"
              onChange={this.handleSelectProduct}
              value={this.props.selectedProductId}
              disabled={
                !this.props.selectedGroupId ||
                (this.props.isOrderEdit &&
                  !EditPermissions.CanEditField(EditableFields.CoverageOptions, this.props.isOpenContract))
              }
              validators={{
                selection: this.isSelected
              }}
            >
              {this.renderProductOptions()}
              {this.renderOptions(this.props.products, true)}
            </Control.select>
            <Errors
              className="error"
              model=".product.id"
              show={{ touched: true, focus: false }}
              messages={getDefaultErrorMessages()}
            />
          </div>
          {this.props.orderType !== ContractOrderType.DTC && (
            <div className="col-md-12 mb-10">
              <CheckboxInline>
                <input
                  type="checkbox"
                  onChange={e => e}
                  onClick={() => this.props.toggleSellersCoverage()}
                  checked={this.props.hasSellersCoverage}
                  disabled={
                    (!this.props.isOrderEdit && !this.canToggleSellersCoverage()) ||
                    (this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.SellersCoverage, this.props.isOpenContract))
                  }
                />
                <label htmlFor="coverageForm.sellerCoverage">
                  Seller's Coverage
                  {isSellersCoverageRequiredState(this.props.stateAbbreviation) && !this.props.isOrderEdit
                    ? ' (Included for State)'
                    : ''}
                  {(isSellersCoverageRequiredState(this.props.stateAbbreviation) && !this.props.isOrderEdit) ||
                  this.isSellersCoverageAvailable()
                    ? ''
                    : ' (Unavailable)'}
                </label>
              </CheckboxInline>
            </div>
          )}
        </div>

        {/* ends here */}
        <div className="col-md-1">
          <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
            Save
          </button>
        </div>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/coverage/_components/product-n-coverages.component.tsx